import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { ButtonLinkAsSimpleText } from '~/prix/react/components/button'

import Map, { MapMouseEvent } from '~/components/map/map'

import Select from 'react-select'

import { colors, sizeColors } from '~/design'
import { SpaceDelimitation } from '../variable/spaceTime.types'
import useAction from '~/prix/react/hooks/action'

import { featureCollection } from '@turf/turf'
import { point } from '@turf/turf'
import { FeatureCollection, Feature } from 'geojson'

import Marker from '~/components/map/marker'
import { smartTitleCase } from '~/prix/naturalLanguage/smartTitleCase'
import PulseLoaderComponent from '~/prix/react/components/pulseLoader'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import { maskCpf } from '~/prix/utils/cpf'
import { pdf } from '@react-pdf/renderer'
import generateItinerarySentinelDefinition from '../appSebrae/generateItinerarySentinel/generateItinerary.definition'
import selectItineraryDefinition from '../appSebrae/selectItinerary/selectItinerary.definition'

import { ExpressionType } from '~/prix/types/expression'
import ItineraryReportPdfComponent from './itinerary/pdf/ItineraryReportPdf.component'
import { Tab, TabList, TabPanel, Tabs } from '~/components/tabs'
import {
  faFilePdf,
  faCircleInfo,
  faList,
  faMapPin,
  faPaperPlane,
  faRepeat,
  faRoute,
  faTrophy,
  faUserCheck,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ButtonVariants } from '~/components/buttonVariants'
import { toastSuccess } from '~/components/toast'
import { anythingToDashCase } from '~/prix'
import { ItineraryMap } from './itinerary/components/ItineraryMap.component'
import { EntityPropertyList } from './itinerary/components/EntityPropertyList.component'
import { extraPropertiesFormatterMap } from './itinerary/components/constants'
import { extractPropertyFromExpression, fetchMapPosition } from './itinerary/components/utils'
import { stringifyDate } from '~/prix/types/date'
import AlertMessage from '~/components/alertMessage'

const Wrapper = styled.div`
  overflow-y: auto;
  max-height: 80vh;
  padding: 15px;

  > main {
    border-radius: 8px;
    border-bottom: 0px;
    overflow: hidden;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    max-width: 100%;
    max-height: 74vh;
  }
`

const Title = styled.h2`
  margin-top: 0;
  color: ${colors.sebraeBlue};
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: space-between;

  .title-content {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
`

const WrapperTabContent = styled.div`
  display: flex;
  flex-direction: column;

  p {
    margin-top: 0;
  }
`

const BottomWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadow(1)};
  margin-bottom: 24px;

  h3 {
    margin: 0;
  }
`

const SizeColor = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 4px;
`

const WarningAlertWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
  flex-shrink: 0;
`

const WarningAlertButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0;
  background: transparent;
  color: ${colors.sebraeBlue};
  border: none;
  cursor: pointer;
  position: relative;
  font-size: 20px;
  font-weight: 600;
`

const WarningDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: -10px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadow(1.5)};
  padding: 16px 12px 12px;
  min-width: 280px;
  max-width: 360px;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  color: ${({ theme }) => theme.colors.darkGrey};
  text-align: left;
  line-height: 1.4;
  font-size: 14px;
  font-weight: 400;

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    right: 12px;
    width: 12px;
    height: 12px;
    background-color: #fff;
    transform: rotate(45deg);
  }
`

export default function ModalSpaceItinerary({
  space,
  filter,
}: {
  onCloseModal: () => void
  space: SpaceDelimitation
  filter: ExpressionType
}) {
  const filteredSizes = React.useMemo(
    () => extractPropertyFromExpression(filter, 'size').map(item => item.value),
    [filter],
  )
  const filteredSegments = React.useMemo(() => {
    const fromSegmentId = extractPropertyFromExpression(filter, 'segmentId').map(item => item.value)
    if (fromSegmentId.length > 0) {
      return fromSegmentId
    }
    const fromSegmentSegmentId = extractPropertyFromExpression(filter, 'segment.segmentId').map(
      item => item.value,
    )
    if (fromSegmentSegmentId.length > 0) {
      return fromSegmentSegmentId
    }

    return fromSegmentId
  }, [filter])

  const agentPolygonCpf = React.useMemo(
    () => extractPropertyFromExpression(filter, 'agentPolygonCpf')[0],
    [filter],
  )
  const targetPolygon = 'delimitationCustom' in space ? space.delimitationCustom : undefined
  const delimitationId = 'delimitationId' in space ? space.delimitationId : undefined

  const modalTopRef = useRef<HTMLDivElement>(null)
  const warningButtonRef = useRef<HTMLButtonElement>(null)
  const warningDropdownRef = useRef<HTMLDivElement>(null)

  const [isPdfGenerating, setIsPdfGenerating] = useState(false)
  const [isWarningDropdownOpen, setIsWarningDropdownOpen] = useState(false)

  const [customStartPoint, setCustomStartPoint] = useState<{
    lat: number
    lng: number
  } | null>(null)

  const placeMarker = useCallback((event: MapMouseEvent) => {
    setCustomStartPoint((event as { lngLat: { lat: number; lng: number } }).lngLat)
  }, [])

  const [selectedItinerary, setSelectedItinerary] = useState<FeatureCollection>()

  const [selectedCustomStartPoint, setSelectedCustomStartPoint] = useState<{
    lat: number
    lng: number
  } | null>(null)

  const {
    result: itineraryGenerationResult,
    isLoading: itineraryGenerationIsLoading,
    error: itineraryGenerationError,
  } = useAction(
    generateItinerarySentinelDefinition,
    selectedCustomStartPoint
      ? {
          latitude: selectedCustomStartPoint?.lat,
          longitude: selectedCustomStartPoint?.lng,
          sizes: filteredSizes,
          segments: filteredSegments,
        }
      : agentPolygonCpf?.value
        ? {
            sizes: filteredSizes,
            segments: filteredSegments,
            agentPolygonCpf: agentPolygonCpf?.value,
            targetPolygon,
          }
        : {
            level: space.delimitationType,
            geoId: delimitationId,
            sizes: filteredSizes,
            segments: filteredSegments,
            agentPolygonCpf: agentPolygonCpf?.value,
            targetPolygon,
          },
    [space.delimitationType, delimitationId, selectedCustomStartPoint],
  )

  const [attendantSelected, setAttendantSelected] = useState<
    | undefined
    | null
    | {
        value: string
        label: string
      }
  >(agentPolygonCpf)

  const handleSelectAttendant = useCallback(
    (value: { value: string; label: string } | undefined) => {
      setAttendantSelected(value)
    },
    [],
  )

  const {
    callAction: callActionSendItinerary,
    result: sendingItineraryResult,
    error: sendingItineraryError,
  } = useActionFunction(selectItineraryDefinition)

  const [isSendingItinerary, setIsSendingItinerary] = useState(false)
  const [itinerarySendingStarted, setItinerarySendingStarted] = useState<undefined | boolean>()

  const sendItineraryToSelectedAttendant = useCallback(async () => {
    setIsSendingItinerary(true)
    const { resultPromise } = callActionSendItinerary({
      temporaryHash: (selectedItinerary as any)?.properties?.['temporaryHash'],
      cpf: attendantSelected?.value,
      isFromManager: true,
    })
    await resultPromise
      .catch(() => {
        setIsSendingItinerary(false)
      })
      .then(() => {
        // setItinerarySendingStarted(true)
        setSelectedItinerary(undefined)
        setAttendantSelected(undefined)
        setItinerarySendingStarted(undefined)

        toastSuccess(`Itinerário enviado para agente ${attendantSelected?.label}`)
      })
      .finally(() => {
        setIsSendingItinerary(false)
      })
  }, [attendantSelected])

  useLayoutEffect(() => {
    const element = modalTopRef.current
    if (element) {
      element.scrollTop = 0
    }
  }, [selectedItinerary])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        warningDropdownRef.current &&
        warningButtonRef.current &&
        !warningDropdownRef.current.contains(event.target as Node) &&
        !warningButtonRef.current.contains(event.target as Node)
      ) {
        setIsWarningDropdownOpen(false)
      }
    }

    if (isWarningDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isWarningDropdownOpen])

  /**Retorna em formato de tabela as propriedades mapeadas em `extraPropertiesFormatterMap`
   * Respeitando a opção `isCountable` retornando um componente tabela contendo a quantidade de pontos
   * de interesse com a mesma categoria.
   */
  const counters = useMemo(() => {
    function getCounters(
      features: { properties: Record<string, any> | null }[],
      label: string,
    ): { [key: string]: number } {
      return features
        .filter(item => item.properties?.kind === 'interestPoint')
        .map(item => item.properties?.[label])
        .reduce((map: any, val) => {
          map[val] = (map[val] || 0) + 1
          return map
        }, {})
    }
    if (selectedItinerary && selectedItinerary.features) {
      const countableProperties = Object.entries(extraPropertiesFormatterMap)
        .filter(([, formatter]) => formatter.isCountable)
        .map(([key, formatter]) => ({ ...formatter, key }))
        .map(countObject => {
          return {
            counterInformation: getCounters(selectedItinerary.features, countObject.key),
            ...countObject,
          }
        })
      return countableProperties.map((countObject, index) => (
        <React.Fragment key={index}>
          <h3 style={{ marginBottom: '10px' }}>{countObject.plural} presentes na rota</h3>
          <CardContent>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr
                  style={{
                    textAlign: 'left',
                    borderBottom: `2px solid ${colors.gray}`,
                  }}
                >
                  <th
                    style={{
                      width: '50%',
                      color: colors.sebraeBlue,
                      paddingTop: 7,
                      paddingBottom: 7,
                    }}
                  >
                    {countObject.singular}
                  </th>
                  <th
                    style={{
                      width: '50%',
                      color: colors.sebraeBlue,
                      paddingTop: 7,
                      paddingBottom: 7,
                    }}
                  >
                    Quantidade
                  </th>
                </tr>
              </thead>
              {Object.keys(countObject.counterInformation)
                .sort(
                  (a, b) => countObject.counterInformation[b] - countObject.counterInformation[a],
                )
                .map((item, index) => (
                  <tbody key={index}>
                    <tr
                      style={{
                        borderBottom:
                          index === Object.keys(countObject.counterInformation).length - 1
                            ? 'none'
                            : `2px solid ${colors.gray}`,
                      }}
                    >
                      <td
                        style={{
                          padding: '7px',
                          paddingLeft: 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '7px',
                        }}
                      >
                        {countObject.label === 'Porte' ? (
                          <SizeColor
                            style={{ backgroundColor: sizeColors[item] || sizeColors.Others }}
                          />
                        ) : null}
                        {item}
                      </td>
                      <td style={{ padding: '7px', paddingLeft: 0 }}>
                        {countObject.counterInformation[item]}
                      </td>
                    </tr>
                  </tbody>
                ))}
            </table>
          </CardContent>
        </React.Fragment>
      ))
    }
  }, [selectedItinerary])

  const handleDownloadPdf = useCallback(async () => {
    if (!selectedItinerary || isPdfGenerating || !attendantSelected) return

    setIsPdfGenerating(true)

    const generatedAt = new Date()
    const fileName = `${anythingToDashCase(attendantSelected.label ?? 'file')}-${stringifyDate(generatedAt)}.pdf`

    const downloadBlob = (blob: Blob) => {
      const objectUrl = window.URL.createObjectURL(blob)
      const anchor = window.document.createElement('a')
      anchor.href = objectUrl
      anchor.download = fileName
      window.document.body.appendChild(anchor)
      anchor.click()
      window.document.body.removeChild(anchor)
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000)
    }

    try {
      console.log(attendantSelected)

      const pdfBlob = await pdf(
        <ItineraryReportPdfComponent
          itinerary={selectedItinerary}
          attendant={{
            attendantCpf: attendantSelected.value,
            attendantName: attendantSelected.label,
          }}
          space={space}
          generatedAt={generatedAt}
        />,
      ).toBlob()
      downloadBlob(pdfBlob)
    } catch (error: unknown) {
      console.error(error)
    } finally {
      setIsPdfGenerating(false)
    }
  }, [selectedItinerary, isPdfGenerating, space, attendantSelected])

  const customStartPointTabContent = useMemo(
    () => (
      <WrapperTabContent>
        <p>
          Clique {selectedCustomStartPoint ? 'em outro' : 'no'} ponto de partida e gere novos
          itinerários.
        </p>
        <div style={{ height: 367 }}>
          <Map
            mapProvider={'mapbox'}
            {...(customStartPoint
              ? fetchMapPosition(
                  featureCollection([point([customStartPoint.lng, customStartPoint.lat])]),
                )
              : itineraryGenerationError || !itineraryGenerationResult?.itineraries
                ? {
                    //Centro do Brazil
                    center: {
                      lat: -13.182533103756843,
                      lng: -52.89874109295445,
                    },
                    zoom: 4,
                  }
                : fetchMapPosition(itineraryGenerationResult?.itineraries[0]))}
            mapType={'roadmap'}
            tilt={0}
            is3D={false}
            onClick={placeMarker}
          >
            {customStartPoint ? <Marker positions={customStartPoint} /> : null}
          </Map>
        </div>

        <BottomWrapper>
          <ButtonVariants
            variant='primary'
            icon={faRepeat}
            onClick={() => setSelectedCustomStartPoint(customStartPoint)}
            disabled={!customStartPoint}
          >
            Gerar novos itinerários
          </ButtonVariants>
        </BottomWrapper>
      </WrapperTabContent>
    ),
    [
      selectedCustomStartPoint,
      customStartPoint,
      itineraryGenerationError,
      itineraryGenerationResult,
      placeMarker,
    ],
  )

  return (
    <Wrapper ref={modalTopRef}>
      {itineraryGenerationIsLoading ? (
        <div
          style={{
            maxHeight: '80vh',
            minHeight: '60vh',
            display: 'flex',
          }}
        >
          <div style={{ margin: 'auto' }}>
            <PulseLoaderComponent />
            <span>Gerando sugestões de itinerários... </span>
          </div>
        </div>
      ) : (
        <>
          {selectedItinerary ? (
            <div style={{ minHeight: '600px' }}>
              {isSendingItinerary ? (
                <div style={{ display: 'flex', minHeight: '600px', alignItems: 'center' }}>
                  <div style={{ margin: 'auto' }}>
                    <PulseLoaderComponent />
                    <span>Enviando itinerário para o agente selecionado...</span>
                  </div>
                </div>
              ) : (
                <>
                  {sendingItineraryError && itinerarySendingStarted ? (
                    <>
                      <AlertMessage
                        type='error'
                        title='Erro ao gerar lista de itinerários possíveis'
                        description={sendingItineraryError?.message}
                      />
                      <BottomWrapper>
                        <ButtonLinkAsSimpleText
                          onClick={() => {
                            setSelectedItinerary(undefined)
                            setAttendantSelected(undefined)
                            setItinerarySendingStarted(undefined)
                          }}
                        >
                          Voltar e escolher outro itinerário
                        </ButtonLinkAsSimpleText>
                      </BottomWrapper>
                    </>
                  ) : (
                    <>
                      <Title>
                        <FontAwesomeIcon icon={faUserCheck} />
                        Escolha o agente para este roteiro
                      </Title>
                      <CardContent>
                        <h3>Lista de agentes disponíveis na área</h3>
                        <div>
                          <Select
                            maxMenuHeight={150}
                            options={itineraryGenerationResult?.attendantsInTheArea.map(
                              (item: {
                                attendantCpf: string
                                attendantName?: string
                                attendantId: string
                              }) => {
                                return {
                                  value: item.attendantCpf,
                                  label: item.attendantName
                                    ? smartTitleCase(item.attendantName)
                                    : `Sem nome: ${maskCpf(item.attendantCpf)}`,
                                }
                              },
                            )}
                            placeholder={'Filtrar agentes'}
                            defaultValue={null}
                            value={attendantSelected}
                            onChange={option =>
                              handleSelectAttendant(
                                option ? { value: option?.value, label: option?.label } : undefined,
                              )
                            }
                            isLoading={itineraryGenerationIsLoading}
                            isClearable
                          />

                          <BottomWrapper>
                            <ButtonVariants
                              variant='transparent'
                              onClick={() => setSelectedItinerary(undefined)}
                            >
                              Cancelar e escolher outro itinerário
                            </ButtonVariants>
                            <ButtonVariants
                              variant='primary'
                              type='button'
                              onClick={handleDownloadPdf}
                              isLoading={isPdfGenerating}
                              disabled={isPdfGenerating || !attendantSelected}
                            >
                              {isPdfGenerating ? (
                                'Gerando relatório'
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faFilePdf} />
                                  Gerar arquivo
                                </>
                              )}
                            </ButtonVariants>
                            {/* <ButtonVariants
                              variant='primary'
                              icon={faPaperPlane}
                              onClick={() => sendItineraryToSelectedAttendant()}
                              disabled={!attendantSelected}
                            >
                              Enviar rota
                            </ButtonVariants> */}
                          </BottomWrapper>
                        </div>
                      </CardContent>
                    </>
                  )}
                </>
              )}

              {!isSendingItinerary && (
                <>
                  <Title>
                    <FontAwesomeIcon icon={faList} />
                    Resumo do itinerário escolhido
                  </Title>
                  <h3>Visualização em mapa</h3>
                  <CardContent>
                    <div style={{ height: 300 }}>
                      <ItineraryMap itinerary={selectedItinerary} />
                    </div>
                  </CardContent>

                  {counters?.map((counterComponent, index) => (
                    <React.Fragment key={index}>{counterComponent}</React.Fragment>
                  ))}

                  <h3 style={{ marginBottom: '10px' }}>
                    Empresas presentes na rota por ordem de atendimento
                  </h3>
                  {selectedItinerary.features
                    .filter((item: Feature) => item.properties?.kind === 'interestPoint')
                    .sort(
                      (a: Feature, b: Feature) =>
                        Number(a.properties?.['position']) - Number(b.properties?.['position']),
                    )
                    .map((item: Feature, index: number) => (
                      <CardContent
                        key={index}
                        style={
                          selectedItinerary.features.filter(
                            (item: Feature) => item.properties?.kind === 'interestPoint',
                          ).length -
                            1 ===
                          index
                            ? { marginBottom: 0 }
                            : {}
                        }
                      >
                        <h3>{item.properties?.['name']}</h3>
                        <div style={{ display: 'flex' }}>
                          <div
                            style={{ minWidth: '50%', overflow: 'hidden', textOverflow: 'clip' }}
                          >
                            <EntityPropertyList
                              feature={item}
                              propertyList={['cnpj', 'tradeName', 'corporateName']}
                            />
                          </div>
                          <div
                            style={{ minWidth: '50%', overflow: 'hidden', textOverflow: 'clip' }}
                          >
                            <EntityPropertyList
                              feature={item}
                              propertyList={['size', 'segmentDescription', 'mainCnaeDescription']}
                            />
                          </div>
                        </div>
                      </CardContent>
                    ))}
                </>
              )}
            </div>
          ) : (
            <>
              {itineraryGenerationError ? (
                <>
                  <Title>
                    <FontAwesomeIcon icon={faMapPin} />
                    {selectedCustomStartPoint
                      ? 'Quer escolher outro ponto de partida?'
                      : 'Quer escolher um ponto de partida?'}
                  </Title>

                  <AlertMessage
                    type='error'
                    title='Erro ao gerar lista de itinerários possíveis'
                    description={itineraryGenerationError.message}
                    style={{ marginBottom: '12px' }}
                  />

                  {customStartPointTabContent}
                </>
              ) : (
                <>
                  <Title>
                    <span className='title-content'>
                      <FontAwesomeIcon icon={faRoute} />
                      Lista de itinerários possíveis
                    </span>
                    <WarningAlertWrapper>
                      <WarningAlertButton
                        type='button'
                        ref={warningButtonRef}
                        onClick={() => setIsWarningDropdownOpen(prev => !prev)}
                      >
                        <FontAwesomeIcon icon={faCircleInfo} />
                        <WarningDropdown ref={warningDropdownRef} $isOpen={isWarningDropdownOpen}>
                          As empresas atendidas pelo App SEBRAE não serão levadas em conta para a
                          geração dos roteiros.
                        </WarningDropdown>
                      </WarningAlertButton>
                    </WarningAlertWrapper>
                  </Title>
                  {selectedCustomStartPoint ? (
                    <p>Usando a coordenada selecionada como ponto de partida.</p>
                  ) : (
                    <p>Usando a rua com mais atendimentos no ano corrente como ponto de partida.</p>
                  )}

                  <Tabs defaultTab='roteiro-campeao'>
                    <TabList>
                      <Tab
                        id='roteiro-campeao'
                        title='Roteiro campeão'
                        icon={<FontAwesomeIcon icon={faTrophy} />}
                      />
                      <Tab
                        id='roteiros-secundarios'
                        title='Roteiros secundários'
                        icon={<FontAwesomeIcon icon={faRoute} />}
                      />
                      <Tab
                        id='novo-ponto-partida'
                        title='Novo ponto de partida'
                        icon={<FontAwesomeIcon icon={faMapPin} />}
                      />
                    </TabList>

                    <TabPanel id='roteiro-campeao'>
                      <WrapperTabContent>
                        <p>
                          Esse itinerário é baseado nos atendimentos dos agentes desta área,
                          mapeando perfil de empresas mais atendidas recentemente contendo as
                          melhores correspondências.
                        </p>
                        <>
                          <div style={{ height: 350 }}>
                            <ItineraryMap itinerary={itineraryGenerationResult.itineraries[0]} />
                          </div>
                          <BottomWrapper>
                            <ButtonVariants
                              variant='primary'
                              icon={faPaperPlane}
                              onClick={() =>
                                setSelectedItinerary(itineraryGenerationResult.itineraries[0])
                              }
                            >
                              Enviar rota à algum agente desta área
                            </ButtonVariants>
                          </BottomWrapper>
                        </>
                      </WrapperTabContent>
                    </TabPanel>

                    <TabPanel id='roteiros-secundarios'>
                      <WrapperTabContent>
                        <p>
                          Outros itinerários contendo empresas relevantes de acordo com o histórico
                          de atendimentos.
                        </p>

                        <Tabs defaultTab='sugestao-rota-1'>
                          <TabList>
                            {itineraryGenerationResult.itineraries
                              .slice(1)
                              .map((_: any, index: number) => (
                                <Tab
                                  key={index}
                                  id={`sugestao-rota-${index + 1}`}
                                  title={`Sugestão de rota ${index + 1}`}
                                />
                              ))}
                          </TabList>

                          {itineraryGenerationResult.itineraries
                            .slice(1)
                            .map((itinerary: any, index: number) => (
                              <TabPanel key={index} id={`sugestao-rota-${index + 1}`}>
                                <WrapperTabContent>
                                  <div style={{ height: 307 }}>
                                    <ItineraryMap itinerary={itinerary} />
                                  </div>
                                  <BottomWrapper>
                                    <ButtonVariants
                                      variant='primary'
                                      icon={faPaperPlane}
                                      onClick={() => setSelectedItinerary(itinerary)}
                                    >
                                      Enviar rota à algum agente desta área
                                    </ButtonVariants>
                                  </BottomWrapper>
                                </WrapperTabContent>
                              </TabPanel>
                            ))}
                        </Tabs>
                      </WrapperTabContent>
                    </TabPanel>

                    <TabPanel id='novo-ponto-partida'>{customStartPointTabContent}</TabPanel>
                  </Tabs>
                </>
              )}
            </>
          )}
        </>
      )}
    </Wrapper>
  )
}
